import {
  type InvestmentAccountDetail,
  type SubmitInvestmentWireInput,
  type SubmitInvestmentWireOutput,
  SubmitInvestmentWireOutputErrorType,
  useSubmitInvestmentWire,
  useWealthOverview,
} from '@egw/api'
import { Alert, AlertType, Button, ButtonVariant, Input, Link, ModalDrawer, useModalState } from '@egw/components'
import { configClient } from '@egw/config'
import { DEFAULT_CURRENCY_CODE, type FeatureFlags } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { Trans, useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import {
  addCentsToCurrency,
  Currency,
  formatInputToCurrency,
  getCurrencySymbol,
  removeCurrencySymbols,
} from '@egw/utils'
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useState, type FC } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { ClientServiceError, ReviewContentSection, TransferReviewSection } from '../../components'
import { AccountTransferDrawer } from '../../components/account-transfer-drawer/account-transfer-drawer'
import { AboutWiresModal } from '../../components/wire-transfer/about-wires-modal/about-wires-modal'
import { InvestmentRecipientInfoForm } from '../../components/wire-transfer/investment-recipient-info-form/investment-recipient-info-form'
import { type Config } from '../../config/config'
import { APEX_WITHDRAWABLE_ACCOUNTS } from '../../constants'
import {
  InvestmentWireTransferField,
  type InvestmentWireTransferFormInterface,
  type TransferListOption,
  type WireTransferField,
} from '../../types/form-model/form-model'
import {
  buildDefaultValuesInvestment,
  buildPersonalPortfolioAccount,
  buildSubmitInvestmentWireInput,
  getAccountTypeFromTypename,
  useGetDisplayValue,
} from '../../utils'

const log = new Logger('Tabbed Wire Page')

const translationPrefix = 'wire_transfer.'

export enum TabbedSendWireState {
  _FORM = 'FORM',
  _REVIEW = 'REVIEW',
  _SUCCESS = 'SUCCESS',
}

export const TabbedSendInvestmentWire: FC<{ setSuccessView: () => void }> = ({ setSuccessView }) => {
  const { flags } = useFlags<FeatureFlags>()
  const { t } = useTranslation('features-transfer')
  const config = configClient.getConfig() as Config

  const { openModal, closeModal, isOpen } = useModalState()

  const { data } = useWealthOverview()

  const apexWithdrawableAccounts = useMemo(
    () =>
      data?.personalPortfolio?.accounts.length
        ? data.personalPortfolio.accounts.filter((account) => {
            const accountType = getAccountTypeFromTypename(account.__typename)
            return (
              APEX_WITHDRAWABLE_ACCOUNTS.includes(accountType) &&
              'attributes' in account &&
              account.attributes?.clientWithdrawalAllowed
            )
          })
        : [],
    [data],
  )

  const [view, setView] = useState<TabbedSendWireState>(TabbedSendWireState._FORM)

  const [showRecipientInfo, setShowRecipientInfo] = useState<boolean>(false)

  const [wireError, setWireError] = useState<SubmitInvestmentWireOutputErrorType | undefined>(undefined)

  // add cents to amount if they are missing
  function addCents(): void {
    const value = formMethods.getValues(InvestmentWireTransferField.amount)
    Boolean(value) && formMethods.setValue(InvestmentWireTransferField.amount, addCentsToCurrency(value))
  }

  const openRecipientInfoModal = (): void => {
    setShowRecipientInfo(true)
  }

  const allFieldOptions = useMemo(
    () => buildPersonalPortfolioAccount(apexWithdrawableAccounts, t, false),
    [apexWithdrawableAccounts, t],
  )

  const formMethods = useForm<InvestmentWireTransferFormInterface>({
    mode: 'all',
    defaultValues: buildDefaultValuesInvestment(),
  })

  const fromFieldOption = formMethods.watch(InvestmentWireTransferField.from)
  const selectedAccountBalance = apexWithdrawableAccounts.find(
    (account) => account.alias === fromFieldOption.alias,
  )?.balance

  useEffect(() => {
    if (apexWithdrawableAccounts.length === 1) {
      formMethods.setValue(InvestmentWireTransferField.from, allFieldOptions[0])
    }
  }, [apexWithdrawableAccounts.length, allFieldOptions, formMethods])

  // Re-trigger amount validation when the selected account changes
  useEffect(() => {
    if (fromFieldOption.alias !== -1 && formMethods.getValues(InvestmentWireTransferField.amount)) {
      void formMethods.trigger(InvestmentWireTransferField.amount)
    }
  }, [fromFieldOption, formMethods])

  const fromFieldDisplayValue = useGetDisplayValue({
    fieldOption: fromFieldOption,
    allFieldOptions,
    isModalOpen: isOpen,
    t,
  })

  const availableBalance = `${getCurrencySymbol(
    selectedAccountBalance?.availableForWithdrawal.currencyCode ?? DEFAULT_CURRENCY_CODE,
  )}${Currency.integerCents.onlyMinus(selectedAccountBalance?.availableForWithdrawal.amount ?? 0)}`

  const { mutate: submitWire, isLoading: isSubmittingWire } = useSubmitInvestmentWire({
    onSuccess: (res: SubmitInvestmentWireOutput, variables?: SubmitInvestmentWireInput) => {
      if (res.__typename === 'SubmitInvestmentWireSuccess' && res.id) {
        sendEvent('user_wireTransfer_accept', {
          eventSource: 'egWealth',
          category: 'wire-transfer',
          subCategory: 'result',
          fromAccount: 'investment',
          accountAlias: variables?.accountAlias,
          recipient: {
            ...variables?.beneficiary,
            ...variables?.recipientBank,
          },
          amount: variables?.money.amount,
        })
        setSuccessView()
      } else if (res.__typename === 'SubmitInvestmentWireOutputError') {
        sendEvent('user_wireTransfer_reject', {
          eventSource: 'egWealth',
          category: 'wire-transfer',
          subCategory: 'result',
          fromAccount: 'investment',
          accountAlias: variables?.accountAlias,
          recipient: {
            ...variables?.beneficiary,
            ...variables?.recipientBank,
          },
          amount: variables?.money.amount,
          failureReason: res.errorType,
        })
        setWireError(res.errorType)
      }
    },
    onError: (e, variables) => {
      sendEvent('user_wireTransfer_error', {
        eventSource: 'egWealth',
        category: 'wire-transfer',
        subCategory: 'result',
        fromAccount: 'investment',
        accountAlias: variables?.accountAlias,
        recipient: {
          ...variables?.beneficiary,
          ...variables?.recipientBank,
        },
        amount: variables?.money.amount,
        error: e,
      })
      setWireError(SubmitInvestmentWireOutputErrorType.BAD_REQUEST)
    },
  })

  const submitWireHandler = (): void => {
    if (fromFieldOption.alias === undefined) {
      setWireError(SubmitInvestmentWireOutputErrorType.BAD_REQUEST)
      return
    }
    const payload = buildSubmitInvestmentWireInput({ wireFormData: formMethods.getValues() })
    // eslint-disable-next-line testing-library/no-debugging-utils
    log.debug('Submitting Wire Transfer', {
      payload,
    })
    void submitWire(payload)
  }

  const wireTransferFormData = formMethods.getValues()

  return (
    <>
      <FormProvider {...formMethods}>
        <form id="wireTransfer" className="pt-0" onSubmit={submitWireHandler}>
          {view === TabbedSendWireState._FORM ? (
            <div className="flex flex-col max-md:justify-between h-full mt-md">
              <AboutWiresModal />
              <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-col gap-3xs">
                  <Input
                    readOnly
                    disabled={apexWithdrawableAccounts.length === 1}
                    autoComplete="off"
                    id={InvestmentWireTransferField.from}
                    onFocus={openModal}
                    label={t('wire_transfer.labels.from')}
                    icon={<SelectDropdownIcon onClick={openModal} />}
                    value={fromFieldDisplayValue ?? ''}
                  />

                  <Controller
                    name={InvestmentWireTransferField.from}
                    control={formMethods.control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value as unknown as string} />}
                  />

                  <p className="font-light text-meta text-xs">
                    {t(`${translationPrefix}available_balance`, { availableBalance })}
                  </p>
                </div>
                <Controller
                  name={InvestmentWireTransferField.amount}
                  control={formMethods.control}
                  rules={{
                    required: t('wire_transfer.recipient_info.errors.required_field'),
                    validate: {
                      sufficientFunds: (value) => {
                        if (!value) return true // Let required validation handle empty values
                        const availableBalance = selectedAccountBalance?.availableForWithdrawal.amount ?? 0
                        return (
                          availableBalance >= removeCurrencySymbols(value) ||
                          t('wire_transfer_review.errors.non_sufficient_funds')
                        )
                      },
                      notZero: (value) => {
                        if (!value) return true // Let required validation handle empty values
                        return removeCurrencySymbols(value) > 0 || t('wire_transfer_review.errors.amount_is_zero')
                      },
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t(buildLabelKey(InvestmentWireTransferField.amount))}
                      onBlur={addCents}
                      valueFormatter={(v) => formatInputToCurrency(v, true)}
                      error={formMethods.formState.errors[InvestmentWireTransferField.amount]?.message}
                      inputMode="decimal"
                      value={field.value ?? ''}
                      id="amount"
                    />
                  )}
                />

                <Input
                  autoComplete="off"
                  id={InvestmentWireTransferField.recipientInfo}
                  onFocus={openRecipientInfoModal}
                  label={t(buildLabelKey(InvestmentWireTransferField.recipientInfo))}
                  icon={<SelectDropdownIcon onClick={openRecipientInfoModal} />}
                  value={formMethods.getValues(InvestmentWireTransferField.recipientInfo)}
                  error={formMethods.formState.errors[InvestmentWireTransferField.recipientInfo]?.message}
                  isPII
                />

                <Controller
                  name={InvestmentWireTransferField.recipientInfo}
                  control={formMethods.control}
                  rules={{
                    required: t('wire_transfer.recipient_info.errors.required_field'),
                  }}
                  render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />

                {/* 
                TODO: ADD REFERENCE BACK IN WHEN WE HAVE IT
                <div>
                  <Controller
                    name={InvestmentWireTransferField.reference}
                    control={formMethods.control}
                    rules={{ required: t('wire_transfer.recipient_info.errors.required_field') }}
                    render={({ field }) => (
                      <>
                        <Textarea
                          {...field}
                          label={t(buildLabelKey(InvestmentWireTransferField.reference))}
                          id="reference-textarea"
                          className="resize-none"
                          maxLength={120}
                          rows={4}
                          value={field.value ?? ''}
                          isPII
                        />
                        <CharCounter id={InvestmentWireTransferField.reference} charLength={120} />
                      </>
                    )}
                  />
                </div> */}
              </div>
              {/*
              TODO: ADD REFERENCE BACK IN WHEN WE HAVE IT
              <RequiredMemoModal /> */}

              <Button
                variant={ButtonVariant.Secondary}
                id="reviewWireTransfer"
                className="md:w-fit mt-2xl"
                isDisabled={!formMethods.formState.isValid}
                onClick={() => {
                  setView(TabbedSendWireState._REVIEW)
                }}
              >
                {t('wire_transfer.review_transfer')}
              </Button>
            </div>
          ) : null}
          {view === TabbedSendWireState._REVIEW ? (
            <div>
              <div className="flex flex-col mt-md">
                <AboutWiresModal />

                <TransferReviewSection
                  sectionTitle={t('wire_transfer_review.from')}
                  sectionContentPrimary={
                    <div className="flex flex-col">
                      <p className="text-text-header typo-primary-base-semibold">{fromFieldDisplayValue}</p>
                      <p className="text-text-meta typo-primary-sm-regular">{fromFieldOption.display.subtitle}</p>
                    </div>
                  }
                />
                <TransferReviewSection
                  sectionTitle={t('wire_transfer_review.amount')}
                  sectionContentPrimary={wireTransferFormData.amount}
                />
                <TransferReviewSection
                  sectionTitle={t('wire_transfer_review.recipient')}
                  sectionContentPrimary={
                    <ReviewContentSection
                      isPII
                      sectionContentPrimary={wireTransferFormData.recipientInfo}
                      sectionContentSecondary={[
                        t('wire_transfer_review.routing', {
                          routingNumber: wireTransferFormData.beneficiaryRoutingNumber,
                        }),
                        t('wire_transfer_review.account', {
                          accountNumber: wireTransferFormData.beneficiaryAccountNumber,
                        }),
                      ]}
                    />
                  }
                />
                {/* 
                TODO: ADD REFERENCE BACK IN WHEN WE HAVE IT
                <div className="flex flex-col gap-2xs pt-md pb-sm justify-between border-b border-light">
                  <p className="typo-primary-base-regular text-text-base">{t('wire_transfer_review.memo_label')}</p>
                  <p className="text-text-header typo-primary-base-regular fs-exclude">
                    {wireTransferFormData.reference}
                  </p>
                </div> */}
              </div>

              <div className="flex flex-col gap-2xl mt-md mb-sm">
                <Alert alertType={AlertType.Info} description={t('wire_transfer_review.info_alert')} />
                <span className="text-text-meta typo-primary-sm-regular">
                  <Trans t={t} i18nKey="wire_transfer_review.wire_transfer_agreement">
                    <Link
                      href={config.zendeskArticles.wireTransferAgreement}
                      className="inline-flex text-text-meta typo-primary-sm-semibold cursor-pointer hover:underline"
                    />
                  </Trans>
                </span>
              </div>
              <div className="flex max-md:flex-col md:flex-row gap-sm">
                <Button
                  size="small"
                  variant={ButtonVariant.Secondary}
                  id="makeChanges"
                  isDisabled={isSubmittingWire}
                  onClick={() => {
                    setView(TabbedSendWireState._FORM)
                  }}
                >
                  {t('wire_transfer_review.make_changes')}
                </Button>
                <Button
                  size="small"
                  variant={ButtonVariant.Primary}
                  id="sendWireTransfer"
                  isDisabled={!formMethods.formState.isValid}
                  isLoading={isSubmittingWire}
                  onClick={submitWireHandler}
                >
                  {t('wire_transfer_review.send_wire_transfer')}
                </Button>
              </div>
            </div>
          ) : null}
          {view === TabbedSendWireState._SUCCESS ? (
            <div className="flex flex-col gap-2">
              <div>
                <div className="relative right-1 mb-2">
                  <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-sm" />
                </div>
                <p className="text-textSuccess text-2xl font-light">{t('wire_transfer_success.transfer_received')}</p>
              </div>
              <div className="text-meta text-base">
                <Trans t={t} i18nKey="wire_transfer_success.p" components={{ 1: <br /> }} />
              </div>
            </div>
          ) : null}
        </form>
      </FormProvider>

      {/* Recipient Info Modal */}
      <ModalDrawer
        title={t('wire_transfer.recipient_info.title')}
        isOpen={showRecipientInfo}
        onClose={() => {
          setShowRecipientInfo(false)
        }}
        id="recipientInfoModal"
      >
        <InvestmentRecipientInfoForm
          setEntireFormData={formMethods.reset}
          entireFormValues={formMethods.getValues()}
          onSubmitCb={() => {
            setShowRecipientInfo(false)
          }}
        />
      </ModalDrawer>

      {/* Error Modal */}
      <ModalDrawer
        title={t('error.modal_title')}
        isOpen={wireError !== undefined}
        onClose={() => {
          setWireError(undefined)
        }}
      >
        <ClientServiceError errorType={wireError} />
      </ModalDrawer>
      <AccountTransferDrawer
        isModalOpen={isOpen}
        onClose={closeModal}
        options={apexWithdrawableAccounts}
        onSelect={(option) => {
          formMethods.setValue(InvestmentWireTransferField.from, option)
          closeModal()
        }}
      />
    </>
  )
}

const buildLabelKey = (key: WireTransferField | InvestmentWireTransferField): string =>
  `${translationPrefix}labels.${key}`

const SelectDropdownIcon: FC<{ onClick: () => void }> = ({ onClick }) => {
  return <ChevronDownIcon onClick={onClick} className="ml-2 h-5 w-5 cursor-pointer text-gray-400" />
}
